import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import { cleanEntity } from 'app/shared/util/entity-utils';
import { EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { ICalendarEntry, defaultValue } from 'app/shared/model/calendarEntry.model';

const initialState: EntityState<ICalendarEntry> = {
  loading: false,
  errorMessage: null,
  entities: [] as ReadonlyArray<ICalendarEntry>,
  entity: defaultValue,
  updating: false,
  updateSuccess: false,
  totalItems: 0,
};

const apiUrl = 'api/calendar';

export const getCalendar = createAsyncThunk(
  'calendar/fetch_calendar',
  async (entity: any) => {
    const requestUrl = `${apiUrl}/annee-scolaire/${entity.classeid}?anneeScolaireId=${entity.anneeScolaireId}`;
    return axios.get<ICalendarEntry[]>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const getCalendarProf = createAsyncThunk('calendar/fetch_calendar_list', async (entity: any) => {
  const requestUrl = `${apiUrl}/professeur/${entity.professeurId}?anneeScolaireId=${entity.anneeScolaireId}`;
  return axios.get<ICalendarEntry[]>(requestUrl);
});

export const getCalendarEntry = createAsyncThunk(
  'calendar_entry/fetch_calendar_entry',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<ICalendarEntry>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createCalendarEntry = createAsyncThunk(
  'calendar_entry/create_calendar_entry',
  async (entity: ICalendarEntry, thunkAPI) => {
    const result = await axios.post<ICalendarEntry>(
      `${apiUrl}/${entity.classeId}?anneeScolaireId=${entity.anneeScolaireId}`,
      cleanEntity(entity)
    );
    thunkAPI.dispatch(getCalendar({ classeid: entity.classeId, anneeScolaireId: entity.anneeScolaireId }));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateCalendarEntry = createAsyncThunk(
  'calendar_entry/update_calendar_entry',
  async (entity: ICalendarEntry, thunkAPI) => {
    const result = await axios.put<ICalendarEntry>(`${apiUrl}/${entity.id}/${entity.classeId}`, cleanEntity(entity));
    thunkAPI.dispatch(getCalendar({ classeid: entity.classeId, anneeScolaireId: entity.anneeScolaireId }));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteCalendarEntry = createAsyncThunk(
  'calendar_entry/delete_calendar_entry',
  async (entity: ICalendarEntry, thunkAPI) => {
    const requestUrl = `${apiUrl}/${entity.id}`;
    const result = await axios.delete<ICalendarEntry>(requestUrl);
    thunkAPI.dispatch(getCalendar({ classeid: entity.classeId, anneeScolaireId: entity.anneeScolaireId }));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteCalendar = createAsyncThunk(
  'classe/delete_classe_calendar',
  async (entity: ICalendarEntry, thunkAPI) => {
    const requestUrl = `${apiUrl}/resetCalendar?classeId=${entity.classeId}&anneeScolaireId=${entity.anneeScolaireId}`;
    const result = await axios.delete<ICalendarEntry>(requestUrl);
    thunkAPI.dispatch(getCalendar({ classeid: entity.classeId, anneeScolaireId: entity.anneeScolaireId }));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export type CalendarState = Readonly<typeof initialState>;

export const CalendarSlice = createSlice({
  name: 'calendarEntry',
  initialState: initialState as CalendarState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getCalendarEntry.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteCalendarEntry.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addCase(deleteCalendar.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(createCalendarEntry, updateCalendarEntry), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getCalendar, getCalendarEntry, getCalendarProf), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isFulfilled(getCalendar, getCalendarProf), (state, action) => {
        state.loading = false;
        state.entities = action.payload.data;
        state.totalItems = parseInt(action.payload.headers['x-total-count'], 10);
      })
      .addMatcher(isPending(createCalendarEntry, updateCalendarEntry, deleteCalendarEntry, deleteCalendar), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = CalendarSlice.actions;

export default CalendarSlice.reducer;
